/* eslint-disable @typescript-eslint/no-misused-promises */
import {Button, Dialog, Intent} from "@blueprintjs/core";
import React, {forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useState, useRef} from "react";
import {AuthContext} from "../../../providers/AuthProvider";
import styled from "@emotion/styled";
import TemporaryPasswordDialog, {TemporaryPasswordDialogRef} from "./TemporaryPasswordDialog";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {UserProfile} from "./UserProfile";
import {User, defaultGroups} from "./UserManagement";
import {createUser, generateTemporaryPassword} from "../../../services/TenantService";
import UserForm from "./UserForm";
import {uniqueId} from "lodash";
import {AppToaster} from "../../../utils/toasters/toaster";
import {useOktaAuth} from "@okta/okta-react";
import log from "loglevel";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import {Constants, UserPreferences} from "../../../constants/constants";
import {useSetUserPrefMutation} from "../../../generated/graphql";
import {createJsonPref, extractSimplePref, usePreferenceContext} from "../../../providers/PreferenceProvider";

interface InviteUserDialogProps {
  refreshData: () => void;
}

export interface InviteUserDialogRef {
  onOpen: () => void;
  onClose: () => void;
  onOpenUpdate: (userToEdit: User) => void;
}

export enum FormMode {
  new,
  edit
}

enum SuccessCodeStatus {
  OK = 200,
  CREATED = 201
}

type UserEditorState = {
  formId: string;
  formData: UserProfile;
  emailValid: boolean;
  emailAlreadyExists: boolean;
  firstNameValid: boolean;
  lastNameValid: boolean;
  legacyUserIdValid: boolean;
  formValid: boolean;
};

type FormStatus = {
  formId: string;
  status: "success" | "failed";
  errorMessage?: string;
};

const produceSuccessMessage = (formMode: FormMode, userValues: UserProfile[]): string => {
  const emailsString = userValues.map((item) => item.email).join(", ");

  return formMode === FormMode.new
    ? `Invited User(s): ${emailsString} to Dispatch! An email has been sent in order to activate their account.`
    : `User: ${emailsString} updated!`;
};

const isAlreadyExistsError = (errorMessage?: string): boolean =>
  !!errorMessage?.toLowerCase().includes("already exists");

const produceFailureMessage = (formMode: FormMode, userValues: UserProfile[]): string => {
  const legacyUserIdString = Array.from(new Set(userValues.map((item) => item.legacyUserId))).join(", ");
  const prefix = `Failed to ${formMode === FormMode.new ? "invite" : "update"} user(s):`;
  const emailsString = userValues.map((item) => item.email).join(", ");
  return `${prefix} ${emailsString}. Please make sure the e-Courier Username: ${legacyUserIdString}, exists in CMS.`;
};

const validateForm = (formState: UserEditorState, fieldName: keyof UserProfile) => {
  const value = formState.formData[fieldName]!;
  switch (fieldName) {
    case "email":
      formState.emailValid = Constants.REGEX.EMAIL_REGEX.test(value as string);
      formState.emailAlreadyExists = false;
      break;
    case "firstName":
      formState.firstNameValid = value.length > 0;
      break;
    case "lastName":
      formState.lastNameValid = value.length > 0;
      break;
    case "legacyUserId":
      formState.legacyUserIdValid = value.length > 0;
      break;
    default:
      break;
  }
  formState.formValid =
    formState.emailValid &&
    !formState.emailAlreadyExists &&
    formState.firstNameValid &&
    formState.lastNameValid &&
    formState.legacyUserIdValid;
};

const MAXIMUM_INVITED_USERS = 10;

const InviteUserDialog = forwardRef<InviteUserDialogRef, InviteUserDialogProps>(({refreshData}, ref) => {
  const {tempPassword: temporaryPasswordEnabled} = useFeatureFlagContext();
  const [isOpen, setIsOpen] = useState(false);
  const authContext = useContext(AuthContext);
  const {userPreferences, userPrefsQueryRefetch} = usePreferenceContext();
  const [setUserPref] = useSetUserPrefMutation();
  const cmsHintDismissed =
    extractSimplePref(userPreferences, UserPreferences.hideCmsUsernameHint, false).value === true;
  const handleDismissCmsHint = useCallback(async () => {
    await setUserPref({variables: {name: UserPreferences.hideCmsUsernameHint, input: createJsonPref(true)}})
      .then(() => {
        userPrefsQueryRefetch?.();
      })
      .catch((error: Error) => {
        log.error(error);
      });
  }, [setUserPref, userPrefsQueryRefetch]);

  const newUserState = useCallback((): UserProfile => {
    return new UserProfile(
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      authContext.tenantId as string,
      defaultGroups(authContext),
      createUser,
      generateTemporaryPassword
    );
  }, [authContext]);

  const createUserForm = useCallback((): UserEditorState => {
    return {
      formId: uniqueId(),
      formData: newUserState(),
      emailValid: false,
      emailAlreadyExists: false,
      firstNameValid: false,
      lastNameValid: false,
      legacyUserIdValid: false,
      formValid: false
    };
  }, [newUserState]);

  const [beforeUpdateUserValues, setBeforeUpdateUserValues] = useState<UserProfile>();
  const [userForms, setUserForms] = useState<UserEditorState[]>([createUserForm()]);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isGeneratingTempPassword, setIsGeneratingTempPassword] = useState<boolean>(false);
  const tempPasswordDialogRef = useRef<TemporaryPasswordDialogRef>(null);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.new);
  const [showResendButton, setShowResendButton] = useState(false);
  const {oktaAuth} = useOktaAuth();

  const handleUserFormChange = (formId: string, fieldName: keyof UserProfile, value: any) => {
    const editingFormIndex = userForms.findIndex((item) => item.formId === formId);
    let editingFormState = {...userForms[editingFormIndex]};
    editingFormState = {...editingFormState, formData: {...editingFormState.formData, [fieldName]: value}};
    validateForm(editingFormState, fieldName);
    const newUserEditorFormState = [...userForms];
    newUserEditorFormState[editingFormIndex] = editingFormState;
    setUserForms(newUserEditorFormState);
  };

  const resetForm = useCallback(() => {
    setFormMode(FormMode.new);
    setUserForms([createUserForm()]);
  }, [createUserForm]);

  const onOpen = useCallback(() => {
    setIsOpen(true);
    resetForm();
  }, [resetForm]);

  const onClose = useCallback(() => {
    setShowResendButton(false);
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  const onOpenUpdate = useCallback((userToEdit: User) => {
    setIsOpen(true);
    const userProfile = userToEdit.profile;
    setBeforeUpdateUserValues(userProfile);
    setFormMode(FormMode.edit);
    setUserForms([
      {
        formId: uniqueId(),
        formData: userProfile,
        emailValid: true,
        emailAlreadyExists: false,
        firstNameValid: true,
        lastNameValid: true,
        legacyUserIdValid: true,
        formValid: true
      }
    ]);
  }, []);

  const handleAddNewForm = useCallback(() => {
    setShowResendButton(false);
    setUserForms((prev) => [...prev, createUserForm()]);
  }, [createUserForm]);

  const handleRemoveForm = useCallback((formId: string) => {
    setUserForms((prev) => prev.filter((item) => item.formId != formId));
  }, []);

  useImperativeHandle(ref, () => ({
    onClose,
    onOpen,
    onOpenUpdate
  }));

  const handleUserEditorSubmit = async () => {
    setIsUpdating(true);
    const invitedFormsStatus: FormStatus[] = [];
    await Promise.all(
      userForms.map((item) =>
        item.formData
          .persistHandler(oktaAuth.getAccessToken() ?? "", item.formData)
          .then((r) => {
            const isSuccess = r.status === SuccessCodeStatus.OK || r.status === SuccessCodeStatus.CREATED;
            invitedFormsStatus.push({
              formId: item.formId,
              status: isSuccess ? "success" : "failed"
            });
          })
          .catch((e: any) => {
            invitedFormsStatus.push({
              formId: item.formId,
              status: "failed",
              errorMessage: typeof e.response?.data === "string" ? e.response.data : JSON.stringify(e.response?.data)
            });
            log.error(e);
          })
      )
    );
    if (invitedFormsStatus.every((item) => item.status === "success")) {
      AppToaster.show({
        intent: Intent.SUCCESS,
        icon: "tick",
        message: produceSuccessMessage(
          formMode,
          userForms.map((item) => item.formData)
        )
      });
      refreshData();
      onClose();
    } else {
      const successFormIds = invitedFormsStatus.filter((item) => item.status === "success").map((item) => item.formId);
      const failedStatuses = invitedFormsStatus.filter((item) => item.status === "failed");
      const alreadyExistsFormIds = failedStatuses
        .filter((item) => isAlreadyExistsError(item.errorMessage))
        .map((item) => item.formId);
      const otherFailedStatuses = failedStatuses.filter((item) => !isAlreadyExistsError(item.errorMessage));
      if (successFormIds.length > 0) {
        const successFormDatas = userForms
          .filter((item) => successFormIds.includes(item.formId))
          .map((item) => item.formData);
        AppToaster.show({
          intent: Intent.SUCCESS,
          icon: "tick",
          message: produceSuccessMessage(formMode, successFormDatas)
        });
      }
      if (otherFailedStatuses.length > 0) {
        const otherFailedFormDatas = userForms
          .filter((item) => otherFailedStatuses.some((status) => status.formId === item.formId))
          .map((item) => item.formData);
        AppToaster.show({
          intent: Intent.WARNING,
          icon: "warning-sign",
          message: produceFailureMessage(formMode, otherFailedFormDatas)
        });
      }
      if (formMode === FormMode.new) {
        setShowResendButton(true);
      }
      setUserForms((prev) =>
        prev
          .filter((item) => !successFormIds.includes(item.formId))
          .map((item) =>
            alreadyExistsFormIds.includes(item.formId) ? {...item, emailAlreadyExists: true, formValid: false} : item
          )
      );
    }
    setIsUpdating(false);
  };

  const handleGenerateTempPassword = async () => {
    setIsGeneratingTempPassword(true);
    try {
      const response = await userForms[0].formData.generateTemporaryPassword!(
        oktaAuth.getAccessToken() ?? "",
        userForms[0].formData.id
      );
      const tempPassword = response.data.temporaryPassword;
      tempPasswordDialogRef.current?.open(tempPassword);
    } catch (error) {
      log.error(error);
      AppToaster.show({
        intent: Intent.DANGER,
        icon: "error",
        message: `Failed to generate temporary password for ${beforeUpdateUserValues?.email}. Please try again later.`
      });
    } finally {
      setIsGeneratingTempPassword(false);
    }
  };

  const isFormDirtyAndValid = useMemo(() => {
    if (formMode === FormMode.edit) {
      return userForms[0].formValid && JSON.stringify(beforeUpdateUserValues) !== JSON.stringify(userForms[0].formData);
    } else {
      return userForms.every((item) => item.formValid);
    }
  }, [beforeUpdateUserValues, formMode, userForms]);

  return (
    <StyledDialog
      isOpen={isOpen}
      title={
        formMode === FormMode.new
          ? "Invite User to Dispatch"
          : `Edit ${beforeUpdateUserValues?.firstName} ${beforeUpdateUserValues?.lastName}`
      }
      onClose={onClose}
      icon={<FontAwesomeIcon style={{paddingRight: "10px"}} icon={solid("user-plus")} />}
      canEscapeKeyClose
      isCloseButtonShown
    >
      <DialogBody data-testid="create-user-dialog">
        <CustomerName>Customer: {`${authContext.tenant?.shortName}`}</CustomerName>
        <FormList>
          {userForms.map((form) => (
            <UserForm
              key={form.formId}
              formMode={formMode}
              formId={form.formId}
              formData={form.formData}
              emailValid={form.emailValid}
              emailAlreadyExists={form.emailAlreadyExists}
              cmsHintDismissed={cmsHintDismissed}
              onDismissCmsHint={handleDismissCmsHint}
              onFormChange={handleUserFormChange}
              onRemoveForm={userForms.length > 1 ? handleRemoveForm : undefined}
            />
          ))}
        </FormList>
        {formMode === FormMode.new && (
          <AddNewButton
            data-testid="add-new-user-form"
            disabled={userForms.length >= MAXIMUM_INVITED_USERS}
            variant="minimal"
            icon="plus"
            onClick={handleAddNewForm}
          >
            Add new user
          </AddNewButton>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          loading={isUpdating}
          data-testid={"create-user-submit-btn"}
          disabled={!isFormDirtyAndValid || isUpdating}
          text={showResendButton ? "Resend Invitation" : formMode === FormMode.new ? "Send Invitation" : "Update User"}
          onClick={handleUserEditorSubmit}
          intent={Intent.PRIMARY}
        />
        {temporaryPasswordEnabled && formMode === FormMode.edit && !authContext.isEcourierUser && (
          <Button
            loading={isGeneratingTempPassword}
            disabled={isGeneratingTempPassword}
            data-testid={"generate-temp-password-btn"}
            text={"Generate Temporary Password"}
            onClick={handleGenerateTempPassword}
          />
        )}
      </DialogFooter>
      <TemporaryPasswordDialog ref={tempPasswordDialogRef} />
    </StyledDialog>
  );
});

InviteUserDialog.displayName = "InviteUserDialog";

export default InviteUserDialog;

const StyledDialog = styled(Dialog)`
  border-radius: 12px;
  overflow: hidden;
  height: 600px;
  background: #fff;
  display: flex;
  font-family: Roboto;
`;

const CustomerName = styled.div`
  font-weight: 500;
  font-size: 16px;
`;

const DialogBody = styled.div`
  padding: 10px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
  overflow: hidden;
`;

const FormList = styled.div`
  overflow-y: auto;
`;

const DialogFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 0 50px 24px;

  .bp5-button {
    border-radius: 6px;
  }
`;

const AddNewButton = styled(Button)`
  width: fit-content;
`;
